import * as React from 'react';
import * as Moment from 'moment';
import Loading, { FontSize } from '../../misc/components/loading';
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { Authorization } from '../../misc/components/Authorization';
import { LocalizedDateTime, DateTimeAccuracy } from '../../misc/components/LocalizedDateTime';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { isColumnHidden } from '../isColumnHidden';
import { Link, useNavigate } from 'react-router-dom';
import { StartSessionWizard } from '../StartSessionWizard/StartSessionWizard';
import { useSecurityContext } from '../../Core/SecurityContext';
import { ConfigurableDataItemIds } from '../../misc/api/ApiConfigurableDataItems';
import { useIdleTimerContext } from '../../Core/IdleTimerContext';
import { ErrorBoundary } from '../../errors/ErrorBoundary';
import { Column, Table } from '../../Components/QueryTable';
import { DateMatchSortHeader } from '../../Components/DateMatchSortHeader';
import { MatchSortHeader } from '../../Components/MatchSortHeader';
import { useQuery } from '../../Components/Query';
import { Subject } from 'webportalclient';
import { useWebPortalApiClient } from '../../misc/api/WebPortalTokenClient';
import { useReferenceContextProvider } from '../../Core/ReferenceContextProvider';
import { uniqBy } from '../../misc/ArrayFunctions';
import { SortHeader } from '../../Components/SortHeader';
import { NumericRangeMatchSortHeader } from '../../Components/NumericRangeMatchSortHeader';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faUsers } from '@fortawesome/free-solid-svg-icons';
import { HamburgerMenu } from '../../misc/components/HamburgerMenu';

declare const rootUrl: string;

interface SubjectsTableOwnProps {
}


interface SubjectsTableProps extends SubjectsTableOwnProps {

}

export const SubjectsTable = ({ }: SubjectsTableProps): JSX.Element => {

    const { data, applyFilters } = useQuery();
    const { dataItemConfigurationCollection, studySubjectSetting, study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    const { timer } = useIdleTimerContext();
    const navigate = useNavigate();
    const [startSessionVisibleForSubjectId, setStartSessionVisibleForSubjectId] = React.useState<string>();
    const client = useWebPortalApiClient();
    const { genders, smokingStatuses } = useReferenceContextProvider();
    const [subjectIdOptions, setSubjectIdOptions] = React.useState([]);
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [randomizationOptions, setRandomizationOptions] = React.useState([]);
    const [genderOptions, setGenderOptions] = React.useState([]);
    const [smokerTypeOptions, setSmokerTypeOptions] = React.useState([]);
    const [populationGroupsOptions, setPopulationGroupsOptions] = React.useState([]);

    const handleClearFilters = () => applyFilters([]);
    const menuFormatter = (
        row: Subject
    ) => {
        return (
            <Authorization permissionIdsAny={[PermissionEnum.ViewSubjects, PermissionEnum.ViewSiteSubjects, PermissionEnum.EditSubject, PermissionEnum.EditSiteSubjects, PermissionEnum.StartSession]} studyId={row.site.studyId} currentPermissions={currentUserPermissions}>
                <div className="text-center">
                    <HamburgerMenu>
                        <Authorization
                            permissionIdsAny={[PermissionEnum.ViewSubjects, PermissionEnum.ViewSiteSubjects]}
                            studyId={row.site.studyId}
                            currentPermissions={currentUserPermissions}
                        >
                            <li>
                                <Link to={`/subjects/view/${row.id}`}>View</Link>
                            </li>
                        </Authorization>
                        <Authorization
                            permissionIdsAny={[PermissionEnum.EditSubject, PermissionEnum.EditSiteSubjects]}
                            studyId={row.site.studyId}
                            currentPermissions={currentUserPermissions}
                        >
                            <li>
                                <Link to={`/Subjects/Edit/${row.id}`}>Edit</Link>
                            </li>
                        </Authorization>
                        <Authorization
                            permissionId={PermissionEnum.StartSession}
                            studyId={row.site.studyId}
                            currentPermissions={currentUserPermissions}
                        >
                            <li>
                                <a style={{ cursor: 'pointer' }} onClick={() => { timer.pause(); setStartSessionVisibleForSubjectId(row.id) }}>Start Session...</a>
                            </li>
                        </Authorization>
                    </HamburgerMenu>
                </div>
            </Authorization>
        );
    };


    const getColumnName = (itemId: ConfigurableDataItemIds) => {
        if (dataItemConfigurationCollection === null)
            return '';

        return dataItemConfigurationCollection.find(x => x.configurableDataItemId === itemId).displayText;
    };

    const getPopulationGroupValue = (row) => {
        if (row.race?.trim()) {
            return <>
                <span data-placement="top" data-toggle="tooltip" title={row.race}>
                    <span>
                        {row.populationGroupName}
                        &nbsp;
                        <FontAwesomeIcon icon={faUsers} className="text-muted" aria-hidden="true" />
                    </span>
                </span>
            </>;
        } else {
            return <>
                {row.populationGroupName ? row.populationGroupName : "-"}
            </>;
        }
    }

    const getLocalizedDateTimeMinute = (n) => {

        if (!n) {
            return "-";
        }
        return <LocalizedDateTime time={n} accuracy={DateTimeAccuracy.Minute} isTooltip={true} />
    }

    const getDateTimeDay = (n) => {

        if (!n) {
            return "-";
        }
        return <time dateTime={n}>{Moment.parseZone(n).format("DD-MMM-YYYY")}</time>
    }

    React.useEffect(() => {

        const getFilterOptions = async () => {

            const subjectsResult = await client.client.getSubjects('number,randomizationNumber,randomized', `site/studyId eq ${study.id}`, undefined, 'site($select=id,number)');
            setSubjectIdOptions(subjectsResult.value.map(s => ({
                value: s.number,
                label: s.number,
            })));

            const sitesResult = await client.client.getSites('id,number', `studyId eq ${study.id}`, "number", undefined);

            setSiteOptions(uniqBy(sitesResult.value.map(s => ({
                value: s.id,
                label: s.number,
            })), (a, b) => a.value === b.value));

            var randomisationIds = subjectsResult.value.filter(e => e.randomizationNumber != null).map(s => ({
                value: s.randomizationNumber,
                label: s.randomizationNumber,
            }));

            randomisationIds.unshift({
                value: null,
                label: '-',
            })
            setRandomizationOptions(randomisationIds);


            var gendersResult = genders.map(s => ({
                value: s.Id,
                label: s.Name,
            }));

            gendersResult.unshift({
                value: null,
                label: '-',
            })
            setGenderOptions(gendersResult);


            var smookingStatuesResult = smokingStatuses.map(s => ({
                value: s.Id,
                label: s.Name,
            }));

            smookingStatuesResult.unshift({
                value: null,
                label: '-',
            })
            setSmokerTypeOptions(smookingStatuesResult);



            var populationGroupsResult = await client.client.getPopulationGroups('id,name', `studyId eq ${study.id}`);
            var populationGroups = populationGroupsResult.value.map(s => ({
                value: s.id,
                label: s.name,
            }));
            populationGroups.unshift({
                value: null,
                label: '-',
            })
            setPopulationGroupsOptions(populationGroups);
        };

        getFilterOptions();
    }, []);
    if (dataItemConfigurationCollection.length === 0) return <Loading />;

    return (
        <ErrorBoundary>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Subjects</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionIdsAny={[PermissionEnum.CreateSubject, PermissionEnum.CreateSiteSubjects]}>
                            <Link id="createNew" className="btn btn-primary" to="/Subjects/Create">
                                Create New
                            </Link>&nbsp;
                        </Authorization>
                        <button className="btn btn-primary" onClick={handleClearFilters}>Clear All Filters</button>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div id="SubjectsTable" className="table-responsive" data-automationid="SubjectsTable">
                                <Table data={data}>
                                    {
                                        isColumnHidden(ConfigurableDataItemIds.SubjectId, dataItemConfigurationCollection) ? undefined :
                                            <Column field="number" header={<MatchSortHeader title={getColumnName(ConfigurableDataItemIds.SubjectId)} options={subjectIdOptions} field="number" sortField="number" />} formatter={n => n ?? '-'} />
                                    }
                                    {
                                        isColumnHidden(ConfigurableDataItemIds.RandomizationNumber, dataItemConfigurationCollection) ? undefined :
                                            <Column field="randomizationNumber" header={<MatchSortHeader title={getColumnName(ConfigurableDataItemIds.RandomizationNumber)} options={randomizationOptions} field="randomizationNumber" sortField="randomizationNumber" />} formatter={n => n ?? '-'} />
                                    }
                                    {
                                        isColumnHidden(ConfigurableDataItemIds.RandomizationDate, dataItemConfigurationCollection) ? undefined :
                                            <Column field="randomized" header={<DateMatchSortHeader title={getColumnName(ConfigurableDataItemIds.RandomizationDate)} field="randomized" sortField="randomized" />} formatter={n => getLocalizedDateTimeMinute(n)} />
                                    }
                                    {
                                        <Column field="site" header={<MatchSortHeader title="Site ID" options={siteOptions} field="site/id" sortField="site/number" />} formatter={n => n.number} />
                                    }
                                    {
                                        isColumnHidden(ConfigurableDataItemIds.SubjectDateOfBirth, dataItemConfigurationCollection) ? undefined :
                                            <Column field="dateOfBirth" header={<DateMatchSortHeader title={getColumnName(ConfigurableDataItemIds.SubjectDateOfBirth)} field="dateOfBirth" sortField="dateOfBirth" />} formatter={n => getDateTimeDay(n)} />
                                    }
                                    {
                                        isColumnHidden(ConfigurableDataItemIds.SubjectAge, dataItemConfigurationCollection) ? undefined :
                                            <Column field="age" header={<NumericRangeMatchSortHeader title={getColumnName(ConfigurableDataItemIds.SubjectAge)} field="age" sortField="age" />} formatter={n => n ?? '-'} />
                                    }
                                    {
                                        isColumnHidden(ConfigurableDataItemIds.SubjectGender, dataItemConfigurationCollection) ? undefined :
                                            <Column field="genderName" header={<MatchSortHeader title={getColumnName(ConfigurableDataItemIds.SubjectGender)} options={genderOptions} field="gender" sortField="genderName" />} formatter={n => n ?? '-'} />
                                    }
                                    {
                                        isColumnHidden(ConfigurableDataItemIds.SubjectHeight, dataItemConfigurationCollection) ? undefined :
                                            <Column field="height" header={<SortHeader title={getColumnName(ConfigurableDataItemIds.SubjectHeight)} field="height" />} formatter={n => n ?? '-'} />
                                    }
                                    {
                                        isColumnHidden(ConfigurableDataItemIds.SubjectWeight, dataItemConfigurationCollection) ? undefined :
                                            <Column field="weight" header={<SortHeader title={getColumnName(ConfigurableDataItemIds.SubjectWeight)} field="weight" />} formatter={n => n ?? '-'} />
                                    }
                                    {
                                        isColumnHidden(ConfigurableDataItemIds.SubjectPopulationGroup, dataItemConfigurationCollection) ? undefined :
                                            <Column field="" header={<MatchSortHeader title={getColumnName(ConfigurableDataItemIds.SubjectPopulationGroup)} options={populationGroupsOptions} field="populationGroupId" sortField="populationGroupName" />} formatter={n => getPopulationGroupValue(n)} />
                                    }
                                    {
                                        isColumnHidden(ConfigurableDataItemIds.SubjectSmokerType, dataItemConfigurationCollection) ? undefined :
                                            <Column field="smokerTypeName" header={<MatchSortHeader title={getColumnName(ConfigurableDataItemIds.SubjectSmokerType)} options={smokerTypeOptions} field="smokerType" sortField="smokerTypeName" />} formatter={n => n ?? '-'} />
                                    }
                                    {
                                        !studySubjectSetting?.canEarlyTerminateSubject ? undefined :
                                            <Column field="terminationDate" header={<DateMatchSortHeader title="Termination Date" field="terminationDate" sortField="terminationDate" />} formatter={n => getLocalizedDateTimeMinute(n)} />
                                    }
                                    <Column field="" formatter={menuFormatter} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {startSessionVisibleForSubjectId ? <StartSessionWizard
                subjectId={startSessionVisibleForSubjectId}
                onWizardClose={(redirect) => {
                    setStartSessionVisibleForSubjectId(undefined);
                    timer.resume();
                    if (redirect) {
                        navigate(redirect);
                    }
                }} /> : null}
        </ErrorBoundary>
    );
};
